/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { AxisBottom, AxisLeft } from "@visx/axis";
import { LinearGradient } from "@visx/gradient";
import { GridRows } from "@visx/grid";
import { Group } from "@visx/group";
import { scaleLinear, scaleBand } from "@visx/scale";
import { BarRounded } from "@visx/shape";
import { Tooltip, useTooltip } from "@visx/tooltip";
import dayjs from "dayjs";
import { v4 as uuidv4 } from "uuid";
import { theme } from "@nordcloud/gnui";
import { Maybe } from "~/generated/graphql";
import { ChartDataDisplayWrapper } from "~/components/NoData";
import { dateFormat } from "~/constants";
import { useCurrency } from "~/hooks";
import { periodDayJs } from "~/services/customers";
import { isEmpty, formatMoney, applyRate, formatMoneyLabel } from "~/tools";

type Event = {
  createdAt: string;
  content: string;
};

type BarsProps = {
  width: number;
  height: number;
  spends: Maybe<string>[];
  events?: Event[];
};
const verticalMargin = 40;

const xTicksBreakpoint = 1000;

const tooltipTimeout = 300;

const formatDate = (n: number): string =>
  `${periodDayJs().format(dateFormat.monthShort)} ${n + 1}`;

export function SpendsChart({ width, height, spends, events = [] }: BarsProps) {
  const {
    showTooltip,
    hideTooltip,
    tooltipOpen,
    tooltipData,
    tooltipLeft,
    tooltipTop,
  } = useTooltip();
  const [hovered, setHovered] = React.useState<number | null>(null);
  const [timeoutId, setTimeoutId] = React.useState(-1);

  const xMax = width - 70;
  const yMax = height - verticalMargin;

  const hasNoData =
    isEmpty(spends) || spends.every((spend) => Number(spend) === 0);

  const { currency } = useCurrency();

  const spendsNumbers = React.useMemo(
    () =>
      [...new Array(periodDayJs().daysInMonth()).keys()].map((k) =>
        applyRate(parseFloat(spends?.[k] || "0"), currency)
      ),
    [spends, currency]
  );

  const spendsValues = React.useMemo(
    () =>
      [...new Array(periodDayJs().daysInMonth()).keys()].map((k) =>
        formatMoney(spends?.[k] || "0", currency)
      ),
    [spends, currency]
  );

  const spendsEvents = React.useMemo(
    () =>
      [...new Array(periodDayJs().daysInMonth()).keys()].map(
        (k) =>
          events
            ?.map((event) => {
              return {
                content: event.content,
                createdAt: event.createdAt,
                day: parseInt(dayjs(event.createdAt).format("DD"), 10) - 1,
              };
            })
            .filter(({ day }) => day === k)
      ),
    [events]
  );

  const xScale = React.useMemo(
    () =>
      scaleBand<number>({
        range: [1, xMax],
        round: true,
        domain: spendsNumbers?.map((_, index) => index),
        padding: 0.1,
      }),
    [xMax, spendsNumbers]
  );

  const yScale = React.useMemo(
    () =>
      scaleLinear<number>({
        range: [yMax, 0],
        round: true,
        domain: [
          hasNoData ? 0 : Math.floor(Math.min(...spendsNumbers)),
          hasNoData ? 250 : Math.ceil(Math.max(...spendsNumbers)),
        ],
      }),
    [yMax, spendsNumbers, hasNoData]
  );

  const onMouseLeave = () => {
    const id = window.setTimeout(() => {
      hideTooltip();
      setHovered(null);
    }, tooltipTimeout);
    setTimeoutId(id);
  };

  const ticks = React.useMemo(
    () =>
      width > xTicksBreakpoint
        ? periodDayJs().daysInMonth()
        : periodDayJs().daysInMonth() / 3,
    [width]
  );

  return (
    <ChartDataDisplayWrapper hasData={!hasNoData}>
      <svg width={width} height={height}>
        <LinearGradient
          to={theme.color.support.grey}
          from={theme.color.support.grey}
          id="barFill"
        />
        <GridRows
          scale={yScale}
          width={xMax}
          top={verticalMargin / 2}
          left={60}
          strokeDasharray="1,3"
          stroke={theme.color.border.focus}
          strokeOpacity={1}
          pointerEvents="none"
          numTicks={5}
        />
        <Group top={verticalMargin / 2} left={60}>
          <AxisLeft
            top={0}
            scale={yScale}
            stroke={theme.color.border.focus}
            tickStroke="transparent"
            numTicks={4}
            tickFormat={(n) => formatMoneyLabel(n, currency)}
            tickLabelProps={() => ({
              fill: theme.color.text.text02,
              fontSize: 10,
              textAnchor: "end",
            })}
          />
          <AxisBottom
            top={yMax}
            scale={xScale}
            stroke={theme.color.border.focus}
            tickStroke={theme.color.border.focus}
            numTicks={ticks}
            tickFormat={formatDate}
            tickLabelProps={() => ({
              fill: theme.color.text.text02,
              fontSize: 8,
              textAnchor: "middle",
            })}
          />
          {spendsNumbers?.map((day, index) => {
            const barWidth = xScale.bandwidth();
            const barHeight = yMax - (yScale(day) ?? 0);
            const barX = xScale(index) || 0;
            const barY = yMax - barHeight - 1; // -1 is for the Axis
            const hasEvents = !!spendsEvents?.[index]?.length;
            return (
              <React.Fragment key={`bar-${index}`}>
                <BarRounded
                  radius={16}
                  x={barX}
                  y={barY}
                  width={barWidth}
                  height={barHeight}
                  fill="url(#barFill)"
                  opacity={hovered !== null && hovered !== index ? 0.6 : 1}
                  onMouseLeave={onMouseLeave}
                  onMouseOver={(event) => {
                    if (timeoutId) {
                      clearTimeout(timeoutId);
                    }
                    setHovered(index);
                    const data = spendsValues[index];
                    showTooltip({
                      tooltipLeft: event.clientX,
                      tooltipTop: event.clientY - 20,
                      tooltipData: [data],
                    });
                  }}
                />
                {hasEvents && (
                  <circle
                    key={`dot-${Number(index)}`}
                    cx={barX}
                    cy={yMax - 5}
                    r={8}
                    fill={theme.color.background.ui05}
                    transform={`translate(${barWidth / 2}, ${4})`}
                    fillOpacity={0.9}
                    strokeWidth={0}
                    z={10}
                    onMouseLeave={onMouseLeave}
                    onMouseMove={(event) => {
                      if (timeoutId) {
                        clearTimeout(timeoutId);
                      }
                      showTooltip({
                        tooltipData: spendsEvents[index]?.map((e) => (
                          <>
                            {dayjs(e.createdAt).format(dateFormat.hour)} -
                            {e.content}
                            <br />
                          </>
                        )),
                        tooltipTop: event.clientY,
                        tooltipLeft: event.clientX,
                      });
                    }}
                  />
                )}
              </React.Fragment>
            );
          })}
        </Group>
      </svg>
      {tooltipOpen && (
        <Tooltip
          key={uuidv4()} // update tooltip bounds each render
          top={tooltipTop}
          left={tooltipLeft}
          style={{
            backgroundColor: theme.color.background.ui05,
            color: theme.color.text.text04,
            position: "fixed",
            padding: `${theme.spacing.spacing01} ${theme.spacing.spacing02}`,
            fontSize: "0.875rem",
            borderRadius: theme.radius.sm,
            transform: "translateY(-100%)",
          }}
        >
          <>{tooltipData}</>
        </Tooltip>
      )}
    </ChartDataDisplayWrapper>
  );
}
